import { Component, input, ChangeDetectionStrategy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDividerModule } from "@angular/material/divider";

type ItemType = 'Workflow' | 'Instruction' | 'Agent';

@Component({
  selector: 'marketplace-controller',
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatFormFieldModule
  ],
  templateUrl: './marketplace-controller.html',
  styleUrl: './marketplace-controller.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketplaceController implements OnInit {
  parentForm = input.required<FormGroup>();
  itemType = input.required<ItemType>();

  // Signal to track whether marketplace settings should be visible
  showMarketplaceSettings = signal<boolean>(false);

  // Business rules:
  // 1) isAdvertised is user-toggleable and bound to the parent form via formControlName
  // 2) Marketplace settings (isAutoApproved and notifyAutoApproval) are only visible when isAdvertised is true
  // 3) isAutoApproved is enabled/disabled based on isAdvertised state:
  //    - When isAdvertised is false: isAutoApproved is disabled and set to false
  //    - When isAdvertised is true: isAutoApproved is enabled and user-toggleable
  // 4) notifyAutoApproval is enabled/disabled based on isAutoApproved state:
  //    - When isAutoApproved is false: notifyAutoApproval is disabled and set to false
  //    - When isAutoApproved is true: notifyAutoApproval is enabled and user-toggleable

  ngOnInit(): void {
    const form = this.parentForm();
    if (!form) return;

    // Ensure isAdvertised exists and is enabled — it is the user-toggleable control.
    if (!form.get('isAdvertised')) {
      form.addControl('isAdvertised', new FormControl<boolean>(false));
    } else {
      form.get('isAdvertised')!.enable();
    }

    // Ensure isAutoApproved exists with initial disabled state (since isAdvertised defaults to false)
    if (!form.get('isAutoApproved')) {
      form.addControl('isAutoApproved', new FormControl<boolean>({ value: false, disabled: true }));
    }

    // Ensure notifyAutoApproval exists with initial disabled state (since isAutoApproved defaults to false)
    if (!form.get('notifyAutoApproval')) {
      form.addControl('notifyAutoApproval', new FormControl<boolean>({ value: false, disabled: true }));
    }

    const isAdvertisedControl = form.get('isAdvertised');
    const isAutoApprovedControl = form.get('isAutoApproved');
    const notifyControl = form.get('notifyAutoApproval');

    // Initialize showMarketplaceSettings signal based on current isAdvertised value
    if (isAdvertisedControl) {
      this.showMarketplaceSettings.set(isAdvertisedControl.value === true);
    }

    // Set initial state of isAutoApproved based on current isAdvertised value
    if (isAdvertisedControl && isAutoApprovedControl) {
      const currentAdvertisedValue = isAdvertisedControl.value;
      if (currentAdvertisedValue) {
        isAutoApprovedControl.enable();
      } else {
        isAutoApprovedControl.setValue(false, { emitEvent: false });
        isAutoApprovedControl.disable();
      }
    }

    // Set initial state of notifyAutoApproval based on current isAutoApproved value
    if (isAutoApprovedControl && notifyControl) {
      const currentAutoApprovedValue = isAutoApprovedControl.value;
      if (currentAutoApprovedValue) {
        notifyControl.enable();
      } else {
        notifyControl.setValue(false, { emitEvent: false });
        notifyControl.disable();
      }
    }

    // Subscribe to isAdvertised changes to enable/disable isAutoApproved accordingly
    isAdvertisedControl?.valueChanges.subscribe((isAdvertised: boolean) => {
      // Update visibility signal
      this.showMarketplaceSettings.set(isAdvertised);

      if (isAutoApprovedControl) {
        if (isAdvertised) {
          isAutoApprovedControl.enable();
        } else {
          isAutoApprovedControl.setValue(false, { emitEvent: true }); // emitEvent: true to trigger isAutoApproved subscription
          isAutoApprovedControl.disable();
        }
      }
    });

    // Subscribe to isAutoApproved changes to enable/disable notifyAutoApproval accordingly
    isAutoApprovedControl?.valueChanges.subscribe((isAutoApproved: boolean) => {
      if (notifyControl) {
        if (isAutoApproved) {
          notifyControl.enable();
        } else {
          notifyControl.setValue(false, { emitEvent: false });
          notifyControl.disable();
        }
      }
    });
  }

}
